export interface GenerateAccuracyFormState {
  batchId: string | null;
  batchType: string | null;
  testBatchAcknowledged: boolean;
  modelId: string | null;
  /** ai-prompts branch the prompt list is sourced from; null = use the API default. */
  branch: string | null;
  promptPath: string | null;
  description: string;
  isDescriptionManuallyEdited: boolean;
}

export const initialGenerateAccuracyState: GenerateAccuracyFormState = {
  batchId: null,
  batchType: null,
  testBatchAcknowledged: false,
  modelId: null,
  branch: null,
  promptPath: null,
  description: '',
  isDescriptionManuallyEdited: false,
};

export type GenerateAccuracyAction =
  | { type: 'selectBatch'; batchId: string; batchType: string }
  | { type: 'confirmTestBatchSelection'; batchId: string; batchType: string }
  | { type: 'switchToValidationBatch'; batchId: string; batchType: string }
  | { type: 'selectModel'; modelId: string }
  | { type: 'selectBranch'; branch: string }
  | { type: 'selectPrompt'; promptPath: string }
  | { type: 'pruneInvalidPrompt' }
  | { type: 'editDescription'; text: string }
  | { type: 'applyComposedDescription'; text: string }
  | { type: 'resetDescription' };

export const generateAccuracyReducer = (
  state: GenerateAccuracyFormState,
  action: GenerateAccuracyAction
): GenerateAccuracyFormState => {
  switch (action.type) {
    case 'selectBatch':
      // Selecting any batch — including re-selecting the test batch after the
      // confirmation dialog is dismissed — resets the acknowledgement atomically.
      return {
        ...state,
        batchId: action.batchId,
        batchType: action.batchType,
        testBatchAcknowledged: false,
      };

    case 'confirmTestBatchSelection':
      return {
        ...state,
        batchId: action.batchId,
        batchType: action.batchType,
        testBatchAcknowledged: true,
      };

    case 'switchToValidationBatch':
      return {
        ...state,
        batchId: action.batchId,
        batchType: action.batchType,
        testBatchAcknowledged: false,
      };

    case 'selectModel':
      // Changing the model invalidates the previously chosen prompt and the
      // description composed from it — cleared atomically rather than left stale.
      return {
        ...state,
        modelId: action.modelId,
        promptPath: null,
        description: '',
        isDescriptionManuallyEdited: false,
      };

    case 'selectBranch':
      // A prompt path valid on one branch may not exist on another — switching
      // branches invalidates the chosen prompt and its composed description,
      // the same atomic reset selectModel applies.
      if (action.branch === state.branch) {
        return state;
      }
      return {
        ...state,
        branch: action.branch,
        promptPath: null,
        description: '',
        isDescriptionManuallyEdited: false,
      };

    case 'selectPrompt':
      // Changing the prompt invalidates the description composed from it.
      return {
        ...state,
        promptPath: action.promptPath,
        description: '',
        isDescriptionManuallyEdited: false,
      };

    case 'pruneInvalidPrompt':
      return { ...state, promptPath: null };

    case 'editDescription':
      return { ...state, description: action.text, isDescriptionManuallyEdited: true };

    case 'applyComposedDescription':
      return { ...state, description: action.text };

    case 'resetDescription':
      return { ...state, isDescriptionManuallyEdited: false };

    default:
      return state;
  }
};
